import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getInstansi, getTodayAttendanceStatus, getAbsensiList, getIzinList } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Landmark, Clock, MapPin, Calendar, CheckCircle, XCircle, 
  TrendingUp, Award, FileText, Camera, AlertCircle, User,
  Briefcase, Building2, ChevronRight, Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../ui/sonner';

const PegawaiInstansiDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [instansi, setInstansi] = useState(null);
  const [todayStatus, setTodayStatus] = useState(null);
  const [riwayatAbsensi, setRiwayatAbsensi] = useState([]);
  const [izinList, setIzinList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Statistics
  const [stats, setStats] = useState({
    hadir_tepat: 0,
    hadir_terlambat: 0,
    izin: 0,
    mangkir: 0,
    total_hari_kerja: 0
  });

  useEffect(() => {
    loadDashboardData();
    
    // Update time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, [user]);

  const loadDashboardData = async () => {
    if (!user?.instansi_id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Load instansi info
      const instansiData = await getInstansi(user.instansi_id);
      setInstansi(instansiData);

      // Load today's attendance status
      try {
        const statusData = await getTodayAttendanceStatus();
        // Map API fields to dashboard fields
        setTodayStatus({
          sudah_absen_masuk: statusData.has_masuk,
          sudah_absen_pulang: statusData.has_pulang,
          waktu_masuk: statusData.masuk_time,
          waktu_pulang: statusData.pulang_time,
        });
      } catch (e) {
        console.log('No attendance status yet');
      }

      // Load attendance history (current month)
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      
      try {
        const riwayat = await getAbsensiList({
          tanggal_mulai: startDate.toISOString().split('T')[0],
          tanggal_selesai: endDate.toISOString().split('T')[0]
        });
        setRiwayatAbsensi(riwayat || []);
        
        // Calculate stats
        const masukRecords = riwayat.filter(r => r.tipe === 'masuk');
        const tepat = masukRecords.filter(r => r.status === 'hadir_tepat_waktu').length;
        const terlambat = masukRecords.filter(r => r.status === 'hadir_terlambat').length;
        
        setStats(prev => ({
          ...prev,
          hadir_tepat: tepat,
          hadir_terlambat: terlambat,
          total_hari_kerja: Math.max(15, tepat + terlambat) // Estimate working days
        }));
      } catch (e) {
        console.log('No attendance history');
      }

      // Load izin list
      try {
        const izin = await getIzinList();
        const myIzin = izin.filter(i => i.guru_id === user.id);
        setIzinList(myIzin.slice(0, 5));
        
        const approvedIzin = myIzin.filter(i => i.status === 'approved').length;
        setStats(prev => ({ ...prev, izin: approvedIzin }));
      } catch (e) {
        console.log('No izin data');
      }

    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Gagal memuat data dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Calculate attendance score
  const totalHadir = stats.hadir_tepat + stats.hadir_terlambat;
  const skorKehadiran = stats.total_hari_kerja > 0 
    ? Math.round((totalHadir / stats.total_hari_kerja) * 100) 
    : 0;
  const skorKetepatan = totalHadir > 0 
    ? Math.round((stats.hadir_tepat / totalHadir) * 100) 
    : 0;

  // Format time
  const formatTime = (date) => {
    return date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Get greeting based on time
  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 10) return 'Selamat Pagi';
    if (hour < 15) return 'Selamat Siang';
    if (hour < 18) return 'Selamat Sore';
    return 'Selamat Malam';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="pegawai-instansi-dashboard">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-slate-700 to-blue-800 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-blue-200 text-sm">{getGreeting()},</p>
            <h1 className="text-2xl md:text-3xl font-bold">{user?.nama}</h1>
            <div className="flex items-center gap-2 mt-2 text-blue-200">
              <Briefcase className="w-4 h-4" />
              <span className="text-sm">{user?.jabatan_instansi || 'Pegawai'}</span>
              {user?.nama_bidang && (
                <>
                  <span className="mx-1">•</span>
                  <span className="text-sm">{user.nama_bidang}</span>
                </>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl md:text-4xl font-bold font-mono">
              {formatTime(currentTime)}
            </div>
            <p className="text-blue-200 text-sm mt-1">{formatDate(currentTime)}</p>
          </div>
        </div>
      </div>

      {/* Instansi Info Card */}
      {instansi && (
        <Card className="border-slate-200 bg-gradient-to-r from-blue-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Landmark className="w-6 h-6 text-blue-700" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-800">{instansi.nama}</h3>
                <p className="text-sm text-gray-600 mt-1">{instansi.alamat}</p>
                <div className="flex flex-wrap gap-4 mt-3 text-sm">
                  <div className="flex items-center gap-1 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>Jam Kerja: {instansi.jam_masuk} - {instansi.jam_pulang}</span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span>Radius: {instansi.radius_geofence}m</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions & Today Status */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Today's Attendance Status */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Status Absensi Hari Ini
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className={`p-4 rounded-xl text-center ${
                todayStatus?.sudah_absen_masuk 
                  ? 'bg-emerald-50 border border-emerald-200' 
                  : 'bg-gray-50 border border-gray-200'
              }`}>
                <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center ${
                  todayStatus?.sudah_absen_masuk ? 'bg-emerald-100' : 'bg-gray-200'
                }`}>
                  {todayStatus?.sudah_absen_masuk ? (
                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                  ) : (
                    <Clock className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <p className="font-medium mt-2">Absen Masuk</p>
                {todayStatus?.sudah_absen_masuk ? (
                  <p className="text-sm text-emerald-600 font-medium">
                    {todayStatus.waktu_masuk || 'Sudah'}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500">Belum</p>
                )}
              </div>
              
              <div className={`p-4 rounded-xl text-center ${
                todayStatus?.sudah_absen_pulang 
                  ? 'bg-blue-50 border border-blue-200' 
                  : 'bg-gray-50 border border-gray-200'
              }`}>
                <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center ${
                  todayStatus?.sudah_absen_pulang ? 'bg-blue-100' : 'bg-gray-200'
                }`}>
                  {todayStatus?.sudah_absen_pulang ? (
                    <CheckCircle className="w-6 h-6 text-blue-600" />
                  ) : (
                    <Clock className="w-6 h-6 text-gray-400" />
                  )}
                </div>
                <p className="font-medium mt-2">Absen Pulang</p>
                {todayStatus?.sudah_absen_pulang ? (
                  <p className="text-sm text-blue-600 font-medium">
                    {todayStatus.waktu_pulang || 'Sudah'}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500">Belum</p>
                )}
              </div>
            </div>

            {/* Absensi Button */}
            <Button 
              className="w-full mt-4 bg-blue-600 hover:bg-blue-700"
              onClick={() => navigate('/absensi')}
            >
              <Camera className="w-4 h-4 mr-2" />
              {!todayStatus?.sudah_absen_masuk ? 'Absen Masuk Sekarang' : 
               !todayStatus?.sudah_absen_pulang ? 'Absen Pulang Sekarang' : 'Lihat Halaman Absensi'}
            </Button>
          </CardContent>
        </Card>

        {/* Performance Score */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Award className="w-5 h-5 text-blue-600" />
              Kinerja Bulan Ini
            </CardTitle>
            <CardDescription>
              {new Date().toLocaleDateString('id-ID', { month: 'long', year: 'numeric' })}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Skor Kehadiran</span>
                <span className="font-medium">{skorKehadiran}%</span>
              </div>
              <Progress value={skorKehadiran} className="h-2" />
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Skor Ketepatan Waktu</span>
                <span className="font-medium">{skorKetepatan}%</span>
              </div>
              <Progress value={skorKetepatan} className="h-2" />
            </div>
            
            <div className="grid grid-cols-4 gap-2 pt-2">
              <div className="text-center p-2 bg-emerald-50 rounded-lg">
                <p className="text-lg font-bold text-emerald-600">{stats.hadir_tepat}</p>
                <p className="text-xs text-gray-600">Tepat</p>
              </div>
              <div className="text-center p-2 bg-amber-50 rounded-lg">
                <p className="text-lg font-bold text-amber-600">{stats.hadir_terlambat}</p>
                <p className="text-xs text-gray-600">Terlambat</p>
              </div>
              <div className="text-center p-2 bg-blue-50 rounded-lg">
                <p className="text-lg font-bold text-blue-600">{stats.izin}</p>
                <p className="text-xs text-gray-600">Izin</p>
              </div>
              <div className="text-center p-2 bg-red-50 rounded-lg">
                <p className="text-lg font-bold text-red-600">{stats.mangkir}</p>
                <p className="text-xs text-gray-600">Mangkir</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Menu */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Menu Cepat</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2 border-slate-200 hover:bg-blue-50"
              onClick={() => navigate('/absensi')}
            >
              <Camera className="w-6 h-6 text-blue-600" />
              <span>Absensi</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2 border-slate-200 hover:bg-blue-50"
              onClick={() => navigate('/riwayat')}
            >
              <Calendar className="w-6 h-6 text-blue-600" />
              <span>Riwayat</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2 border-slate-200 hover:bg-blue-50"
              onClick={() => navigate('/izin')}
            >
              <FileText className="w-6 h-6 text-blue-600" />
              <span>Izin</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2 border-slate-200 hover:bg-blue-50"
              onClick={() => navigate('/profil')}
            >
              <User className="w-6 h-6 text-blue-600" />
              <span>Profil</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity & Izin */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Recent Attendance */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Riwayat Terakhir
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/riwayat')}>
                Lihat Semua <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {riwayatAbsensi.length > 0 ? (
              <div className="space-y-3">
                {riwayatAbsensi.slice(0, 5).map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        item.tipe === 'masuk' ? 'bg-emerald-100' : 'bg-blue-100'
                      }`}>
                        {item.tipe === 'masuk' ? (
                          <CheckCircle className={`w-5 h-5 ${
                            item.status === 'hadir_tepat_waktu' ? 'text-emerald-600' : 'text-amber-600'
                          }`} />
                        ) : (
                          <Clock className="w-5 h-5 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {item.tipe === 'masuk' ? 'Absen Masuk' : 'Absen Pulang'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(item.waktu_absen).toLocaleDateString('id-ID', { 
                            day: 'numeric', month: 'short' 
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm">
                        {new Date(item.waktu_absen).toLocaleTimeString('id-ID', {
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                      {item.tipe === 'masuk' && (
                        <Badge className={`text-xs ${
                          item.status === 'hadir_tepat_waktu' 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {item.status === 'hadir_tepat_waktu' ? 'Tepat' : 'Terlambat'}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>Belum ada riwayat absensi</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Izin Status */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Status Izin
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/izin')}>
                Lihat Semua <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {izinList.length > 0 ? (
              <div className="space-y-3">
                {izinList.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">{item.jenis_izin}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(item.tanggal_mulai).toLocaleDateString('id-ID', { 
                          day: 'numeric', month: 'short' 
                        })} - {new Date(item.tanggal_selesai).toLocaleDateString('id-ID', { 
                          day: 'numeric', month: 'short' 
                        })}
                      </p>
                    </div>
                    <Badge className={`${
                      item.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                      item.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {item.status === 'approved' ? 'Disetujui' :
                       item.status === 'rejected' ? 'Ditolak' : 'Menunggu'}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>Belum ada pengajuan izin</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3 border-slate-200"
                  onClick={() => navigate('/izin')}
                >
                  Ajukan Izin
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PegawaiInstansiDashboard;
